import { ref, computed } from 'vue';
import { provideLocal, injectLocal, toValue } from '@vueuse/core';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import useAvailability from '~/modules/upc/composables/useAvailability';
import { useProduct } from '~/modules/product/composables/useProduct';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import useColor from '~/modules/color/composables/useColor';
import useSize from '~/modules/size/composables/useSize';
import { useType as useOrientation } from '~/modules/type/composables/useType';
import { usePageData } from '~/composables/usePageData';
import useUpc from '~/modules/upc/composables/useUpc';
import { validateUPC } from '../utils/validateUPC';
import { UPC_VALIDATOR_REPOSITORY } from '../constants/providers';

/** @typedef {import('~/modules/add-to-bag/types').PayloadValidator} PayloadValidator */

export const useUpcValidatorProvide = () => {
  const { killswitches } = useKillswitches();
  const { productId } = useProduct();
  const { colors, selectedColor } = useColor();
  const { sizes, selectedSize, label: sizeName } = useSize();
  const { isSizeAvailable, isColorAvailable } = useAvailability();
  const { types: orientationArray, selectedType: selectedOrientation, label: typeName } = useOrientation(productId);
  const { isMattress3v } = useFeatureEligibility();
  const { traits } = usePageData();

  let selectedUpcData = ref(null);
  try {
    const upc = useUpc(productId);
    selectedUpcData = upc.selectedUpcData || computed(() => upc.upcData?.value?.upcs?.[upc.selectedUpcId?.value]);
  } catch {
    selectedUpcData = ref(null);
  }

  const upcValidator = () => {
    const upcAvailability = toValue(selectedUpcData)?.availability;
    const isAnyFulfillmentAvailable = killswitches.isFulfillmentDrivenAvailabilityEnabled ? upcAvailability?.isAnyFulfillmentAvailable : undefined;

    return validateUPC({
      selectedColor: selectedColor.value,
      selectedSize: selectedSize.value,
      selectedOrientation: selectedOrientation.value,
      colors: colors.value,
      sizes: sizes.value,
      orientations: orientationArray.value,
      isColorAvailable,
      isSizeAvailable,
      typeName: typeName?.value,
      sizeName: sizeName?.value,
      excludeColorLabel: isMattress3v,
      isBcomConsolidatedDropdownCopyUpdate: traits.isBcomConsolidatedDropdownCopyUpdate,
      isAnyFulfillmentAvailable,
    });
  };

  provideLocal(UPC_VALIDATOR_REPOSITORY, upcValidator);
};

export const useUpcValidator = () => {
  /**
   * @type {PayloadValidator<void>}
   */
  const upcValidator = injectLocal(UPC_VALIDATOR_REPOSITORY);
  if (typeof upcValidator === 'undefined') {
    throw new TypeError('useUpcValidator() is called without provider.');
  }

  return { upcValidator };
};
